import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface JoinRequest {
  id: string;
  booth_id: string;
  user_id: string;
  status: string;
  request_date: string;
  responded_at: string | null;
  created_at: string;
  profile?: {
    name?: string;
    photo_url?: string;
  };
}

export const useBoothJoinRequests = (
  boothId: string | undefined,
  isAdmin: boolean
) => {
  const [pendingRequests, setPendingRequests] = useState<JoinRequest[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch pending join requests
  const fetchRequests = useCallback(async () => {
    if (!boothId || !isAdmin) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("exhibition_join_requests")
        .select(`
          *,
          profile:profiles!exhibition_join_requests_user_id_fkey (
            name,
            photo_url
          )
        `)
        .eq("booth_id", boothId)
        .eq("status", "pending")
        .order("created_at", { ascending: true });

      if (error) throw error;

      // Transform the data to match our interface
      const requests: JoinRequest[] = (data || []).map((item: any) => ({
        id: item.id,
        booth_id: item.booth_id,
        user_id: item.user_id,
        status: item.status,
        request_date: item.request_date,
        responded_at: item.responded_at,
        created_at: item.created_at,
        profile: item.profile || undefined,
      }));

      setPendingRequests(requests);
    } catch (error) {
      console.error("[JoinRequests] Error fetching requests:", error);
    } finally {
      setLoading(false);
    }
  }, [boothId, isAdmin]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Subscribe to realtime changes
  useEffect(() => {
    if (!boothId || !isAdmin) return;

    const channel = supabase
      .channel(`booth-requests:${boothId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "exhibition_join_requests",
          filter: `booth_id=eq.${boothId}`,
        },
        () => {
          fetchRequests();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [boothId, isAdmin, fetchRequests]);

  // Admit a user (approve join request)
  const admitUser = useCallback(async (requestId: string) => {
    try {
      const { error } = await supabase
        .from("exhibition_join_requests")
        .update({
          status: "approved",
          responded_at: new Date().toISOString(),
        })
        .eq("id", requestId);

      if (error) throw error;

      setPendingRequests(prev => prev.filter(r => r.id !== requestId));
      toast.success("User admitted to booth");
      return true;
    } catch (error) {
      console.error("[JoinRequests] Error admitting user:", error);
      toast.error("Failed to admit user");
      return false;
    }
  }, []);

  // Reject a join request
  const rejectRequest = useCallback(async (requestId: string) => {
    try {
      const { error } = await supabase
        .from("exhibition_join_requests")
        .update({
          status: "rejected",
          responded_at: new Date().toISOString(),
        })
        .eq("id", requestId);

      if (error) throw error;

      setPendingRequests(prev => prev.filter(r => r.id !== requestId));
      toast.success("Request rejected");
      return true;
    } catch (error) {
      console.error("[JoinRequests] Error rejecting request:", error);
      toast.error("Failed to reject request");
      return false;
    }
  }, []);

  return {
    pendingRequests,
    loading,
    admitUser,
    rejectRequest,
    refetch: fetchRequests,
  };
};
